import { useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Stack, Typography, Button, InputLabel, Input
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTheme } from '@emotion/react';
import { useDispatch } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { collectiveApiRequest } from '../../services';
import { remainingCredits } from '../../store/reducers/auth';
import { ErrorText } from '../errorText';
import { Cloud, CloudRight } from '../../assets';
import { totalCreditsAndRemaining } from '../../services/creditsService';
import PdfViewer from '../../utils/pdfViewer/PdfViewer';
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";

const getVideoDuration = (videoFile) =>
  new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      window.URL.revokeObjectURL(video.src);
      resolve(video.duration);
    };

    video.onerror = () => {
      window.URL.revokeObjectURL(video.src);
      reject(new Error("Error loading video metadata"));
    };

    video.src = URL.createObjectURL(videoFile);
  });

export function UploadImage({
  setImage,
  setJsonData,
  setloader,
  apiResponse,
  image,
  upload,
  apiUrl,
  setDownloadedUrl,
  errorObject,
  setErrorObject,
  file,
  setFile,
  setStatus,
  setCurlBody,
  setCurlUrl,
  setCurlMethod,
  onFileChange,
  text,
  buttonText = "Try API",
}) {
  const form = useForm({ mode: "all" });
  const { handleSubmit } = form;
  const [previewImage, setPreviewImage] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [fileError, setFileError] = useState("");
  const [isPdf, setIsPdf] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const dispatch = useDispatch();

  const handleFileChange = (e, value) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    const isVideo = selectedFile.type.startsWith("video/");
    const isMp4 = selectedFile.type === "video/mp4";
    setFileError("");

    if (isVideo && !isMp4) {
      setFileError("Rejected - Invalid file format");
      setFile((prev) => ({ ...prev, [value]: selectedFile }));
      return;
    }

    if (isVideo && selectedFile.size > 25 * 1024 * 1024) {
      setFileError("Rejected - File size should be less than 25 mb");
      setFile((prev) => ({ ...prev, [value]: selectedFile }));
      return;
    }

    if (isVideo) {
      const video = document.createElement("video");
      video.preload = "metadata";

      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        const duration = video.duration;

        if (duration < 6 || duration > 30) {
          setFileError(
            "Rejected - Video duration should be in 6 sec to 30 secs"
          );
          setFile((prev) => ({ ...prev, [value]: selectedFile }));
        } else {
          setFileError("");
          setFile((prev) => ({ ...prev, [value]: selectedFile }));
        }
      };

      video.onerror = () => {
        setFileError(
          "Error loading video metadata. Please try another video file."
        );
        setFile((prev) => ({ ...prev, [value]: null }));
        window.URL.revokeObjectURL(video.src);
      };

      video.src = URL.createObjectURL(selectedFile);
    } else {
      setFileError("");
      setFile((prev) => ({ ...prev, [value]: selectedFile }));
    }
  };

  useEffect(() => {
    if (file) {
      setPageNumber(1);
      const formData = new FormData();
      formData.append("file", file);
      setImage((prev) => ({ ...prev, uploaded: formData }));
      setDownloadedUrl("");

      setCurlBody(formData);
      setCurlUrl(apiUrl);
      setCurlMethod("POST");
    }
  }, [
    file,
    setImage,
    setDownloadedUrl,
    apiUrl,
    setCurlBody,
    setCurlUrl,
    setCurlMethod,
  ]);

  const handlePreview = (uploadedFile) => {
    const url = URL.createObjectURL(uploadedFile);
    setPreviewImage(url);

    const fileType = uploadedFile?.type?.toLowerCase?.() || "";
    const fileName = uploadedFile?.name?.toLowerCase?.() || "";
    const isPdfFile =
      fileType === "application/pdf" || fileName.endsWith(".pdf");
    const isVideoFile = fileType.startsWith("video/");

    setPageNumber(1);

    if (isPdfFile) {
      setIsPdf(true);
      setIsVideo(false);
    } else if (isVideoFile) {
      setIsVideo(true);
      setIsPdf(false);
    } else {
      setIsPdf(false);
      setIsVideo(false);
    }
    setOpenDialog(true);
  };

  useEffect(() => {
    return () => {
      if (previewImage) {
        URL.revokeObjectURL(previewImage);
      }
    };
  }, [previewImage]);

  const allowedTypes = [
    "image/png",
    "image/jpeg",
    "image/jpg",
    "application/pdf",
    "image/tiff",
    "image/tif",
    "video/mp4",
    "video/avi",
    "video/quicktime",
    "video/x-matroska",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ];

  const handleApiRequest = async () => {
    setIsLoading(true);
    setloader(true);
    setErrorObject(null);

    for (const [, fileItem] of Object.entries(file || {})) {
      if (!fileItem) {
        setFileError("No file selected");
        setIsLoading(false);
        setloader(false);
        return;
      }

      if (!allowedTypes.includes(fileItem?.type)) {
        setFileError(`Invalid File Type.`);
        setIsLoading(false);
        setloader(false);
        return;
      }
    }

    const formData = new FormData();

    if (file) {
      const isBrowserFile =
        (typeof File !== "undefined" && file instanceof File) ||
        (typeof Blob !== "undefined" && file instanceof Blob);
      const isFileMap =
        typeof file === "object" && !Array.isArray(file) && !isBrowserFile;

      if (isFileMap) {
        Object.entries(file).forEach(([value, fileItem]) => {
          formData.append(`${value}`, fileItem);
        });
      } else {
        formData.append("file", file);
      }
    }

    try {
      const res = await collectiveApiRequest(formData, apiUrl);
      setStatus(res?.status);
      if (res?.status === 200) {
        if (apiResponse === "json") {
          setJsonData(res?.data);
        } else {
          const downloadUrl = res?.data?.downloadUrl;
          const status = res?.data?.status;
          setDownloadedUrl(downloadUrl);
          setImage((prev) => ({ ...prev, masked: downloadUrl, status }));
        }

        // Keep credit refresh non-blocking; loader should only track request lifecycle.
        setTimeout(async () => {
          const remainingCredit = await totalCreditsAndRemaining();
          if (remainingCredit?.status === 200) {
            dispatch(
              remainingCredits({
                credits: remainingCredit?.data?.credit?.remainingCredit,
              })
            );
          }
        }, 2000);
      }
    } catch (error) {
      setErrorObject(error?.response?.data);
    } finally {
      setIsLoading(false);
      setloader(false);
    }
  };

  useEffect(() => {
    setErrorObject(null);
    setDownloadedUrl("");
  }, [apiResponse, setErrorObject]);

  useEffect(() => {
    if (errorObject) {
      if (apiResponse === "json") {
        setJsonData(errorObject); 
      } else {
        setImage(() => ({
          error:
            errorObject || errorObject?.error || errorObject?.response?.data,
        }));
      }
    } else {
      setImage((prev) => ({ ...prev, error: null }));
    }
  }, [errorObject, setJsonData, setImage, apiResponse]);

  const isFileUploaded =
    file === null ? false : Object.keys(file)?.length === upload?.length;

  function UploadBox({
    item = {},
    file,
    setFile,
    setFileError,
    handleFileChange,
    handlePreview,
  }) {
    const {
      id,
      label,
      value: itemValue,
      fieldInstruction,
    } = item;
    const theme = useTheme();
    const assignFile = (nextFile) => {
      setFile((prev) => ({ ...prev, [itemValue]: nextFile }));
    };

    const { getRootProps, getInputProps } = useDropzone({
      noClick: true,
      onDrop: async (acceptedFiles) => {
        if (acceptedFiles.length === 0) return;
        const droppedFile = acceptedFiles[0];
        if (!droppedFile) return;

        const fileExt = "." + droppedFile.name.split(".").pop().toLowerCase();   
        const supportedTypes = upload?.length
        ? upload
            .flatMap(item =>
              item.supportedFiles
                ? item.supportedFiles.split(",").map(type => type.trim())
                : []
            )
        : [];
        
        if (supportedTypes.length && !supportedTypes.includes(fileExt)) {
          setFileError(`Invalid file format. Allowed: ${supportedTypes.join(", ")}`);
          assignFile(null);
          return;
        }

        const invalidTypes = ["image/gif"];

        if (invalidTypes.includes(droppedFile.type)) {
          setFileError("Rejected - Invalid file format");
          assignFile(null);
          return;
        }
        const isVideo = droppedFile.type.startsWith("video/");
        const isMp4 = droppedFile.type === "video/mp4";

        setFileError("");
        if (isVideo && !isMp4) {
          setFileError("Rejected - Invalid file format");
          assignFile(droppedFile);
          return;
        }
        if (isVideo && droppedFile.size > 25 * 1024 * 1024) {
          setFileError("Rejected - File size should be less than 25 mb");
          assignFile(droppedFile);
          return;
        }
        if (isVideo) {
          try {
            const duration = await getVideoDuration(droppedFile);
            if (duration < 6 || duration > 30) {
              setFileError(
                "Rejected - Video duration should be between 6 and 30 seconds"
              );
            } else {
              setFileError("");
            }
            assignFile(droppedFile);
          } catch (error) {
            setFileError(
              "Error loading video metadata. Please try another video file."
            );
          }
          return;
        }
        setFileError("");
        assignFile(droppedFile);
      },
      onDropRejected: (fileRejections) => {
        const error = fileRejections[0]?.errors[0];
        if (error?.code === "file-too-large") {
          setFileError("Rejected - File size should be less than 25 mb");
        } else {
          setFileError("Rejected - Invalid file format");
        }
      },
      accept: {
        "image/*": [".png", ".jpg", ".jpeg"],
        "application/pdf": [".pdf"],
        "video/*": [".mp4", ".mov", ".avi", ".mkv"],
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      },
      multiple: false,
    });

    return (
      <Fragment key={id || itemValue}>
        <Typography
          variant="body1"
          sx={{ padding: "3px 0", color: theme.palette.grey.darken4 }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            borderRadius: "12px",
            border: "2px dashed rgba(101, 117, 139, 0.25)",
            minHeight: "120px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
          }}
          {...getRootProps()}
        >
          <InputLabel
            htmlFor={`upload-image-${id}`}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              height: "100%",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.preventDefault();
              document.getElementById(`upload-image-${itemValue}`)?.click();
            }}
          >
            {/* <Box
              
              src={file && file[item?.value] ? CloudRight : Cloud}
            /> */}
            <FileUploadOutlinedIcon sx={{ width: "5rem", height: "5rem" }} />
            <Typography
              variant="h5"
              sx={{
                color: theme.palette.textColor.grey,
                whiteSpace: "break-spaces",
                fontWeight: "400",
                textAlign: "center",
              }}
            >
              {file?.[itemValue]
                ? "File uploaded"
                : "Click to upload  or drag and drop"}
            </Typography>
            {file?.[itemValue] && <div>{file?.[itemValue]?.name}</div>}
          </InputLabel>
          <Input
            id={`upload-image-${itemValue}`}
            type="file"
            style={{ display: "none" }}
            onChange={(e) => handleFileChange(e, itemValue)}
            {...getInputProps()}
          />
        </Box>
        {file?.[itemValue] && (
          <Button
            onClick={() => handlePreview(file?.[itemValue])}
            sx={{
              borderRadius: "10px",
              border: "1px solid #E1E7EF",
              backgroundColor: "#FFF",
              boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            Load Sample Data
          </Button>
        )}
        {fieldInstruction && (
          <Typography
            variant="body1"
            sx={{ color: theme.palette.textColor.grey, marginTop: "5px" }}
          >
            {fieldInstruction}
          </Typography>
        )}
      </Fragment>
    );
  }

  UploadBox.propTypes = {
    item: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      supportedFiles: PropTypes.string,
      fieldInstruction: PropTypes.string,
    }),
    file: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
    setFile: PropTypes.func,
    setFileError: PropTypes.func,
    handleFileChange: PropTypes.func,
    handlePreview: PropTypes.func,
  };

  return (
    <Box sx={{ position: "relative" }}>
      <Stack
        component="form"
        onSubmit={handleSubmit(handleApiRequest)}
        spacing={2}
        sx={{ px: { xs: 2, md: 0 }, py: 2 }}
      >
        {upload?.map(({ id, label, value, supportedFiles, fieldInstruction }) => (
          <UploadBox
            key={id || value}
            item={{ id, label, value, supportedFiles, fieldInstruction }}
            file={file}
            setFile={setFile}
            setFileError={setFileError}
            handleFileChange={handleFileChange}
            handlePreview={handlePreview}
          />
        ))}

        {fileError && <ErrorText>{fileError}</ErrorText>}

        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={{ xs: 1, md: 2 }}
          sx={{ pt: 2 }}
        >
          <Button
            variant="contained"
            type="submit"
            disabled={!isFileUploaded || !!fileError || isLoading}
            sx={{
              display: "flex",
              width: "100%",
              borderRadius: "10px",
              backgroundColor: "var(--lightBlue-500, #03A9F4)",
              boxShadow:
                "0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.10)",
              display: upload?.length && text?.length ? "none" : "flex",
              position: "relative",
            }}
          >
            {isLoading ? (
              "Processing..."
            ) : (
              <>
                <PlayArrowOutlinedIcon sx={{ width: "3rem", height: "3rem" }} />
                {buttonText}
              </>
            )}
          </Button>
        </Stack>
      </Stack>

      <PdfViewer
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        isPdf={isPdf}
        previewImage={previewImage}
        pageNumber={pageNumber}
        setPageNumber={setPageNumber}
        isVideo={isVideo}
        useDialog
        file={file}
      />
    </Box>
  );
}

UploadImage.propTypes = {
  setImage: PropTypes.func,
  setJsonData: PropTypes.func,
  setloader: PropTypes.func,
  apiResponse: PropTypes.string,
  image: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  upload: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
      value: PropTypes.string,
      supportedFiles: PropTypes.string,
      fieldInstruction: PropTypes.string,
    }),
  ),
  apiUrl: PropTypes.string,
  setDownloadedUrl: PropTypes.func,
  errorObject: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  setErrorObject: PropTypes.func,
  file: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.string]),
  setFile: PropTypes.func,
  setStatus: PropTypes.func,
  setCurlBody: PropTypes.func,
  setCurlUrl: PropTypes.func,
  setCurlMethod: PropTypes.func,
  onFileChange: PropTypes.func,
  text: PropTypes.array,
  buttonText: PropTypes.string,
};